import { useState, useRef, useEffect } from "react";
import { API_BASE } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { MessageSquare, X, Send, Sparkles } from "lucide-react";

const SUGGESTIONS = [
  "Why do I need this?",
  "Will ChatGPT recommend me?",
  "Do I need crypto?",
  "Can my developer do this?",
];

export default function SalesAssistant({ context }) {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    { role: "assistant", content: "Hi — I'm the AgenticReady AI Sales Assistant. Ask me anything about getting your business ready for AI customers." },
  ]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const sessionRef = useRef("chat_" + Math.random().toString(36).slice(2, 12));
  const bottomRef = useRef(null);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, open]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || streaming) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: msg }, { role: "assistant", content: "" }]);
    setStreaming(true);
    try {
      const res = await fetch(`${API_BASE}/sales-chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ session_id: sessionRef.current, message: msg, context }),
      });
      const reader = res.body.getReader();
      const dec = new TextDecoder();
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        const chunk = dec.decode(value, { stream: true });
        setMessages((m) => {
          const copy = [...m];
          copy[copy.length - 1] = { role: "assistant", content: copy[copy.length - 1].content + chunk };
          return copy;
        });
      }
    } catch {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { role: "assistant", content: "Sorry, I had trouble responding. Please try again." };
        return copy;
      });
    } finally {
      setStreaming(false);
    }
  };

  return (
    <>
      {!open && (
        <button onClick={() => setOpen(true)} data-testid="sales-assistant-toggle"
          className="fixed bottom-2 lg:bottom-5 right-5 z-50 h-14 w-14 rounded-full bg-indigo-600 hover:bg-indigo-700 text-white shadow-lg shadow-indigo-500/30 flex items-center justify-center transition-transform hover:scale-105">
          <MessageSquare className="h-6 w-6" />
        </button>
      )}
      {open && (
        <div className="fixed bottom-5 right-5 z-50 w-[calc(100vw-2.5rem)] sm:w-96 h-[560px] max-h-[80vh] bg-white rounded-2xl border border-slate-200 shadow-2xl flex flex-col overflow-hidden" data-testid="sales-assistant-panel">
          <div className="flex items-center justify-between px-4 py-3 bg-slate-950 text-white">
            <div className="flex items-center gap-2">
              <div className="h-8 w-8 rounded-full bg-gradient-to-br from-indigo-500 to-violet-500 flex items-center justify-center"><Sparkles className="h-4 w-4" /></div>
              <div>
                <p className="font-heading font-semibold text-sm leading-none">AI Sales Assistant</p>
                <p className="text-[11px] text-emerald-400 mt-0.5">● Online</p>
              </div>
            </div>
            <button onClick={() => setOpen(false)} data-testid="sales-assistant-close"><X className="h-5 w-5 text-slate-400 hover:text-white" /></button>
          </div>
          <div className="flex-1 overflow-y-auto p-4 space-y-3 scrollbar-thin bg-slate-50">
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[85%] rounded-2xl px-3.5 py-2.5 text-sm ${m.role === "user" ? "bg-indigo-600 text-white" : "bg-white border border-slate-200 text-slate-700"}`}>
                  {m.content || <span className="inline-flex gap-1"><span className="h-1.5 w-1.5 bg-slate-400 rounded-full pulse-dot" /><span className="h-1.5 w-1.5 bg-slate-400 rounded-full pulse-dot" style={{ animationDelay: "0.2s" }} /><span className="h-1.5 w-1.5 bg-slate-400 rounded-full pulse-dot" style={{ animationDelay: "0.4s" }} /></span>}
                </div>
              </div>
            ))}
            {messages.length <= 1 && (
              <div className="flex flex-wrap gap-2 pt-2">
                {SUGGESTIONS.map((s) => (
                  <button key={s} onClick={() => send(s)} className="text-xs px-3 py-1.5 rounded-full bg-white border border-slate-200 text-slate-600 hover:border-indigo-400 hover:text-indigo-600 transition-colors">{s}</button>
                ))}
              </div>
            )}
            <div ref={bottomRef} />
          </div>
          <form onSubmit={(e) => { e.preventDefault(); send(); }} className="p-3 border-t border-slate-200 flex gap-2">
            <Input value={input} onChange={(e) => setInput(e.target.value)} placeholder="Ask about pricing, plans…" data-testid="sales-assistant-input" className="h-10" />
            <Button type="submit" disabled={streaming} data-testid="sales-assistant-send" className="h-10 w-10 p-0 bg-indigo-600 hover:bg-indigo-700"><Send className="h-4 w-4" /></Button>
          </form>
        </div>
      )}
    </>
  );
}
